import React, { useCallback, useRef, useState } from 'react'
import { Box, Fade, Typography, keyframes } from '@mui/material'
import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import BlockIcon from '@mui/icons-material/Block'
import { filterMediaFiles, getAcceptedExtensionsDisplay } from '../constants/mediaTypes'

const pulseRing = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-12px); }
`

const fadeInScale = keyframes`
  from { 
    opacity: 0; 
    transform: scale(0.95);
  }
  to { 
    opacity: 1; 
    transform: scale(1);
  }
`

type DragState = 'idle' | 'valid' | 'invalid'

type Props = {
  onFilesDropped: (files: FileList) => void
  onInvalidFiles?: (message: string) => void
  disabled?: boolean
  children?: React.ReactNode
}

export default function DropZone({ onFilesDropped, onInvalidFiles, disabled, children }: Props) {
  const [dragState, setDragState] = useState<DragState>('idle')
  const dragCounterRef = useRef(0)

  // Verifica se os itens sendo arrastados contêm mídia
  const checkDragItems = useCallback((e: React.DragEvent): boolean => {
    const items = e.dataTransfer.items
    if (!items || items.length === 0) return true // Assume válido se não conseguir verificar

    for (let i = 0; i < items.length; i++) {
      const item = items[i]
      if (item.kind === 'file') {
        const type = item.type.toLowerCase()
        if (type.startsWith('audio/') || type.startsWith('video/')) {
          return true
        }
      }
    }
    return false
  }, [])

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (disabled) return

    dragCounterRef.current++

    if (dragCounterRef.current === 1) {
      const isValid = checkDragItems(e)
      setDragState(isValid ? 'valid' : 'invalid')
    }
  }, [disabled, checkDragItems])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    // Importante: definir o dropEffect para mostrar o cursor correto
    e.dataTransfer.dropEffect = dragState === 'valid' ? 'copy' : 'none'
  }, [dragState])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    dragCounterRef.current--

    if (dragCounterRef.current === 0) {
      setDragState('idle')
    }
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()

    // Reset do contador
    dragCounterRef.current = 0
    setDragState('idle')

    if (disabled) return

    const files = e.dataTransfer.files
    if (!files || files.length === 0) return

    // Filtra apenas arquivos de mídia válidos
    const mediaFiles = filterMediaFiles(files)

    if (mediaFiles.length === 0) {
      onInvalidFiles?.('Nenhum arquivo de mídia válido. Envie arquivos de áudio ou vídeo.')
      return
    }

    if (mediaFiles.length < files.length) {
      const ignored = files.length - mediaFiles.length
      onInvalidFiles?.(`${ignored} arquivo(s) ignorado(s). Apenas arquivos de mídia são aceitos.`)
    }

    // Cria uma nova FileList com apenas os arquivos válidos
    const dataTransfer = new DataTransfer()
    mediaFiles.forEach(file => dataTransfer.items.add(file))
    onFilesDropped(dataTransfer.files)
  }, [disabled, onFilesDropped, onInvalidFiles])

  const isDragging = dragState !== 'idle'
  const isValid = dragState === 'valid'

  return (
    <Box
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        minHeight: 400,
        transition: 'all 0.3s ease'
      }}
    >
      {children}

      {/* Overlay de drag com Fade para transição suave */}
      <Fade in={isDragging} timeout={200}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            bgcolor: isValid ? 'rgba(25, 118, 210, 0.06)' : 'rgba(211, 47, 47, 0.06)',
            border: '3px dashed',
            borderColor: isValid ? '#1976d2' : '#d32f2f',
            borderRadius: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(4px)',
            animation: `${fadeInScale} 0.2s ease-out`,
            pointerEvents: 'none' // Evita interferência nos eventos de drag
          }}
        >
          <Box
            sx={{
              bgcolor: isValid ? 'rgba(25, 118, 210, 0.1)' : 'rgba(211, 47, 47, 0.1)',
              borderRadius: '50%',
              p: 3,
              mb: 2,
              animation: isValid ? `${float} 2s ease-in-out infinite` : 'none'
            }}
          >
            {isValid ? (
              <CloudUploadIcon
                sx={{
                  fontSize: 64,
                  color: '#1976d2'
                }}
              />
            ) : (
              <BlockIcon
                sx={{
                  fontSize: 64,
                  color: '#d32f2f'
                }}
              />
            )}
          </Box>

          <Typography
            variant="h5"
            sx={{
              color: isValid ? '#1976d2' : '#d32f2f',
              fontWeight: 600,
              mb: 1
            }}
          >
            {isValid ? 'Solte o arquivo aqui' : 'Tipo de arquivo não suportado'}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: '#666',
              textAlign: 'center',
              maxWidth: 300
            }}
          >
            {isValid
              ? 'Arquivos de áudio e vídeo são suportados'
              : 'Apenas arquivos de mídia (áudio/vídeo) são aceitos'}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: '#999',
              mt: 1.5,
              textAlign: 'center'
            }}
          >
            {getAcceptedExtensionsDisplay()}
          </Typography>
        </Box>
      </Fade>
    </Box>
  )
}
